import React, { useCallback, useContext } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Button } from 'ncoded-component-library';
import ResolvePopup from 'components/ResolvePopup';
import DownloadIcon from 'icons/Download.icon';
import XcircleIcon from 'icons/Xcircle.icon';
import RefreshIcon from 'icons/Refresh.icon';
import ArrowClockIcon from 'icons/ArrowClock.icon';
import SettingsIcon from 'icons/Settings.icon';
import ClipboardcopyIcon from 'icons/Clipboardcopy.icon';
import Counter from 'components/Counter';
import { SIZE } from 'models/Size';
import { DataRoomRFI } from 'router/subrouters/DataRoom/models/DataRoomRfi';
import { RfiActivityAccessDate } from 'router/subrouters/DataRoom/models/DataRoomMember';
import { getIsRfiStatusClosed } from 'router/subrouters/DataRoom/utils';
import DownloadStatusContext from 'providers/DownloadStatus/DownloadStatus.context';
import useCopyToClipboard from 'hooks/useCopyToClipboard';
import { getResolveUrl, getRFILinkRoute } from 'utils';
import { getHasUnreadActivity } from '../RfiActivityDrawer/utils';

import './RfiDetailsHeaderActions.styles.scss';

type RfiDetailsHeaderActionsProps = {
  className?: string;
  rfi?: DataRoomRFI;
  accessDate?: RfiActivityAccessDate;
  isChangeStatusLoading?: boolean;
  onExport: () => void;
  onChangeStatus: () => void;
  onActivityClick: () => void;
  onSettingsClick: () => void;
};

const RfiDetailsHeaderActions: React.FC<RfiDetailsHeaderActionsProps> = (
  props,
) => {
  const {
    className,
    rfi,
    accessDate,
    isChangeStatusLoading,
    onExport,
    onChangeStatus,
    onActivityClick,
    onSettingsClick,
  } = props;

  const { t } = useTranslation();

  const { isRfiCategoriesExportInProgress } = useContext(DownloadStatusContext);

  const copyToClipboard = useCopyToClipboard();

  const isClosed = getIsRfiStatusClosed(rfi?.status);

  const onCopyLink = useCallback(
    (e: React.MouseEvent) =>
      copyToClipboard(e, getResolveUrl(getRFILinkRoute(rfi?.externalKey))),
    [copyToClipboard, rfi?.externalKey],
  );

  const classes = classNames('rfi-details-header-actions', className);

  return (
    <div className={classes}>
      <ResolvePopup
        onHover
        popupContent={t(isClosed ? 'reopen' : 'closeRequest')}
      >
        <Button
          variant="outline"
          icon={
            isClosed ? (
              <RefreshIcon className="rfi-details-header-actions__close-icon" />
            ) : (
              <XcircleIcon className="rfi-details-header-actions__close-icon" />
            )
          }
          aria-label={t(isClosed ? 'reopen' : 'closeRequest')}
          disabled={isChangeStatusLoading}
          onClick={(e) => {
            e.stopPropagation();
            onChangeStatus();
          }}
        />
      </ResolvePopup>

      <ResolvePopup onHover popupContent={t('copyLink')}>
        <Button
          variant="outline"
          icon={ClipboardcopyIcon}
          aria-label={t('copyLink')}
          onClick={onCopyLink}
        />
      </ResolvePopup>

      <ResolvePopup onHover popupContent={t('activity')}>
        <Button
          variant="outline"
          icon={ArrowClockIcon}
          aria-label={t('activity')}
          onClick={(e) => {
            e.stopPropagation();
            onActivityClick();
          }}
        >
          {getHasUnreadActivity(rfi, accessDate) && (
            <Counter onlyDot isPositionAbsolute size={SIZE.SMALL} />
          )}
        </Button>
      </ResolvePopup>

      <ResolvePopup onHover popupContent={t('export')}>
        <Button
          variant="outline"
          icon={
            <DownloadIcon className="rfi-details-header-actions__export-icon" />
          }
          aria-label={t('export')}
          disabled={isRfiCategoriesExportInProgress}
          onClick={(e) => {
            e.stopPropagation();
            onExport();
          }}
        />
      </ResolvePopup>

      <ResolvePopup onHover popupContent={t('settings')}>
        <Button
          variant="outline"
          icon={
            <SettingsIcon className="rfi-details-header-actions__settings-icon" />
          }
          aria-label={t('settings')}
          onClick={(e) => {
            e.stopPropagation();
            onSettingsClick();
          }}
        />
      </ResolvePopup>
    </div>
  );
};

export default RfiDetailsHeaderActions;
